import { useMemo } from 'react';
import { useLocation } from 'react-router';
import type { BreadcrumbItem } from '@/components/ui/breadcrumb';

interface NavItem {
  name: string;
  pathname?: string;
  code?: string;
  breadcrumbLabel?: string;
  showInBreadcrumb?: boolean;
  children?: NavItem[];
}

/** Labels for action segments not in the navigation tree */
const actionLabels: Record<string, string> = {
  create: 'Novo',
  edit: 'Editar',
  details: 'Detalhes',
  clone: 'Clonar',
  form: '', // skip "form" segments silently
  management: '', // skip "management" segments silently (Next.js compat)
  'update-from-order': 'Atualizar do Pedido',
};

/** Module path → display name */
const moduleMap: Record<string, string> = {
  admin: 'Administração',
  production: 'Produção',
  commercial: 'Comercial',
  logistic: 'Logística',
  connectivity: 'Conectividade',
};

/**
 * Flatten the navigation tree into a list of { pathname, chain } entries.
 * Each entry contains the full ancestor chain from root to the item.
 */
function flattenNavTree(
  items: NavItem[],
  chain: NavItem[] = [],
): { pathname: string; chain: NavItem[] }[] {
  const result: { pathname: string; chain: NavItem[] }[] = [];

  for (const item of items) {
    const nextChain = [...chain, item];

    if (item.pathname) {
      result.push({ pathname: item.pathname, chain: nextChain });
    }

    if (item.children) {
      result.push(...flattenNavTree(item.children, nextChain));
    }
  }

  return result;
}

/**
 * Normalize a pathname for comparison:
 * - Remove trailing slashes
 * - Normalize common singular/plural variants
 */
function normalizePath(path: string): string {
  return path.replace(/\/+$/, '');
}

/**
 * Check if `candidate` is a prefix of `target` at a path-segment boundary.
 * e.g. "/commercial" is a prefix of "/commercial/proposals" but not "/commercialX".
 */
function isPathPrefix(candidate: string, target: string): boolean {
  const c = normalizePath(candidate);
  const t = normalizePath(target);
  if (t === c) return true;
  return t.startsWith(`${c}/`);
}

/**
 * Check if two paths are equivalent, accounting for singular/plural route variants.
 * e.g. "/commercial/proposal" matches "/commercial/proposals" because routes may
 * redirect plural to singular or vice-versa.
 */
function pathsMatchFuzzy(a: string, b: string): boolean {
  if (a === b) return true;

  const segA = a.split('/').filter(Boolean);
  const segB = b.split('/').filter(Boolean);

  if (segA.length !== segB.length) return false;

  for (let i = 0; i < segA.length; i++) {
    if (segA[i] === segB[i]) continue;
    // Allow singular/plural: one is the other plus trailing 's'
    const shorter = segA[i].length < segB[i].length ? segA[i] : segB[i];
    const longer = segA[i].length < segB[i].length ? segB[i] : segA[i];
    if (longer !== `${shorter}s`) return false;
  }

  return true;
}

/**
 * Check if `candidate` is a prefix of `target` at a segment boundary,
 * allowing segments to differ by plural/singular (trailing 's').
 */
function isPathPrefixFuzzy(candidate: string, target: string): boolean {
  if (isPathPrefix(candidate, target)) return true;

  const segC = candidate.split('/').filter(Boolean);
  const segT = target.split('/').filter(Boolean);

  if (segC.length > segT.length) return false;

  for (let i = 0; i < segC.length; i++) {
    if (segC[i] === segT[i]) continue;
    // Allow singular/plural: one is the other plus trailing 's'
    const shorter = segC[i].length < segT[i].length ? segC[i] : segT[i];
    const longer = segC[i].length < segT[i].length ? segT[i] : segC[i];
    if (longer === `${shorter}s`) continue;
    return false;
  }

  return true;
}

/**
 * Find the best matching nav chain for the given pathname.
 * Tries exact match first, then fuzzy exact, then longest prefix match.
 */
function findBestNavChain(
  pathname: string,
  flatNav: { pathname: string; chain: NavItem[] }[],
): { chain: NavItem[]; isExact: boolean } | null {
  const normalizedPathname = normalizePath(pathname);
  let bestPrefix: { chain: NavItem[]; pathname: string } | null = null;
  let fuzzyExact: { chain: NavItem[] } | null = null;

  for (const entry of flatNav) {
    const entryPath = normalizePath(entry.pathname);

    // Exact match (highest priority)
    if (entryPath === normalizedPathname) {
      return { chain: entry.chain, isExact: true };
    }

    // Fuzzy exact match (singular/plural variants, e.g. /proposal ↔ /proposals)
    if (!fuzzyExact && pathsMatchFuzzy(entryPath, normalizedPathname)) {
      fuzzyExact = { chain: entry.chain };
    }

    // Strict prefix match
    if (isPathPrefix(entryPath, normalizedPathname)) {
      if (!bestPrefix || entryPath.length > bestPrefix.pathname.length) {
        bestPrefix = { chain: entry.chain, pathname: entryPath };
      }
    }

    // Fuzzy prefix match (e.g. /commercial/proposal/form/create matched by /commercial/proposals)
    if (isPathPrefixFuzzy(entryPath, normalizedPathname)) {
      if (!bestPrefix || entryPath.length > bestPrefix.pathname.length) {
        bestPrefix = { chain: entry.chain, pathname: entryPath };
      }
    }
  }

  // Fuzzy exact match takes priority over prefix match
  if (fuzzyExact) {
    return { chain: fuzzyExact.chain, isExact: true };
  }

  if (bestPrefix) {
    return { chain: bestPrefix.chain, isExact: false };
  }

  return null;
}

export function useBreadcrumb({ navigation }: { navigation: NavItem[] }): BreadcrumbItem[] {
  const location = useLocation();
  const pathname = location.pathname;

  return useMemo(() => {
    if (pathname === '/home' || pathname === '/') {
      return [];
    }

    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.length === 0) return [];

    const breadcrumbs: BreadcrumbItem[] = [];

    // 1. Add module breadcrumb
    const moduleName = moduleMap[pathSegments[0]];
    if (moduleName) {
      breadcrumbs.push({
        label: moduleName,
        href: `/${pathSegments[0]}`,
        isCurrent: pathSegments.length === 1,
      });
    }

    if (pathSegments.length === 1) return breadcrumbs;

    // 2. Flatten nav tree and find best match
    const flatNav = flattenNavTree(navigation);
    const match = findBestNavChain(pathname, flatNav);

    if (match) {
      const { chain, isExact } = match;

      // Skip the first item if it's the module root (already added above)
      const startIdx = chain[0].pathname === `/${pathSegments[0]}` ? 1 : 0;

      for (let i = startIdx; i < chain.length; i++) {
        const item = chain[i];
        if (item.showInBreadcrumb === false) continue;

        const isLast = i === chain.length - 1 && isExact;
        breadcrumbs.push({
          label: item.breadcrumbLabel || item.name,
          href: isLast ? undefined : item.pathname,
          isCurrent: isLast,
        });
      }

      // 3. Handle trailing segments not in the nav tree (create, edit, :id, etc.)
      if (!isExact) {
        const lastNavItem = chain[chain.length - 1];
        const lastNavSegments = (lastNavItem.pathname || '').split('/').filter(Boolean);
        const remaining = pathSegments.slice(lastNavSegments.length);

        for (let i = 0; i < remaining.length; i++) {
          const segment = remaining[i];
          const isLast = i === remaining.length - 1;

          // Skip empty-label segments (e.g. "form", "management")
          if (segment in actionLabels) {
            const label = actionLabels[segment];
            if (label) {
              breadcrumbs.push({
                label,
                isCurrent: isLast,
              });
            }
            continue;
          }

          // UUID or numeric ID segments → "Detalhes" (unless preceded/followed by action)
          if (isUuidOrId(segment)) {
            const nextSegment = remaining[i + 1];
            const prevSegment = remaining[i - 1];
            // Skip if next segment is an action (e.g. :id/edit → action handles the label)
            if (nextSegment && nextSegment in actionLabels) continue;
            // Skip if previous segment was a non-empty action (e.g. edit/:id → already labeled)
            if (prevSegment && prevSegment in actionLabels && actionLabels[prevSegment]) continue;
            breadcrumbs.push({
              label: 'Detalhes',
              isCurrent: isLast,
            });
            continue;
          }

          // Fallback: format segment as readable label (capitalize, replace hyphens)
          const builtPath = `/${pathSegments.slice(0, lastNavSegments.length + i + 1).join('/')}`;
          breadcrumbs.push({
            label: segment
              .split('-')
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' '),
            href: isLast ? undefined : builtPath,
            isCurrent: isLast,
          });
        }
      }
    } else {
      // 4. Fallback: no nav match at all, build from path segments
      for (let i = 1; i < pathSegments.length; i++) {
        const segment = pathSegments[i];
        const isLast = i === pathSegments.length - 1;

        if (segment in actionLabels) {
          const label = actionLabels[segment];
          if (label) {
            breadcrumbs.push({ label, isCurrent: isLast });
          }
          continue;
        }

        if (isUuidOrId(segment)) {
          const nextSegment = pathSegments[i + 1];
          const prevSegment = pathSegments[i - 1];
          // Skip if next segment is an action (e.g. :id/edit → action handles the label)
          if (nextSegment && nextSegment in actionLabels) continue;
          // Skip if previous segment was a non-empty action (e.g. edit/:id → already labeled)
          if (prevSegment && prevSegment in actionLabels && actionLabels[prevSegment]) continue;
          breadcrumbs.push({ label: 'Detalhes', isCurrent: isLast });
          continue;
        }

        // Use segment as label (capitalize, replace hyphens)
        breadcrumbs.push({
          label: segment
            .split('-')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '),
          href: isLast ? undefined : `/${pathSegments.slice(0, i + 1).join('/')}`,
          isCurrent: isLast,
        });
      }
    }

    return breadcrumbs;
  }, [pathname, navigation]);
}

function isUuidOrId(segment: string): boolean {
  return /^[0-9a-f-]{8,}$/i.test(segment) || /^\d+$/.test(segment);
}
